import * as Sentry from '@sentry/react';
import { getCookieConsent } from '@utils/helpers';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = process.env.NODE_ENV;

const ignoredDomainsAndPaths = [
	'facebook.com',
	'fbevents',
	'google-analytics.com',
	'analytics.google',
	'analytics',
	'api.hubapi.com',
	'hscollectedforms',
	'/api/auth/',
	'/admin/',
];

const ignoredErrors = ['g.readyState'];

const sensitiveRoutes = ['/login', '/register'];

// Regular expression for matching email addresses
const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

// Function to recursively scrub emails from objects
const scrubEmails = obj => {
	try {
		if (!obj) return obj;
		if (typeof obj === 'string') {
			return obj.replace(EMAIL_REGEX, '[EMAIL REDACTED]');
		}
		if (typeof obj !== 'object') return obj;

		const newObj = Array.isArray(obj) ? [] : {};
		for (const key in obj) {
			newObj[key] = scrubEmails(obj[key]);
		}
		return newObj;
	} catch (error) {
		console.warn('Error scrubbing emails:', error);
		return obj; // Return original object if scrubbing fails
	}
};

const config = {
	dsn: SENTRY_DSN,
	environment: ENVIRONMENT,
	ignoreTransactions: ignoredDomainsAndPaths.map(domainOrPath => new RegExp(domainOrPath)),

	integrations: [
		Sentry.browserTracingIntegration(),
		Sentry.breadcrumbsIntegration({
			console: false, // Disable console in breadcrumbs
		}),
		Sentry.captureConsoleIntegration({
			levels: ['error'], // Only capture error level
		}),
		Sentry.replayIntegration({
			captureConsole: false, // Disable console capture in replay
			beforeAddRecordingEvent: event => {
				// Filter out console.log events
				if (
					event.data.payload.category === 'console' &&
					event.data.payload.level === 'log'
				) {
					return null;
				}
				return event;
			},
		}),
	],

	ignoreErrors: ignoredErrors,
	beforeSend(event) {
		// Early return if no request data
		if (!event.request) return event;

		const { request } = event;
		const fieldsToScrub = ['data', 'query_string', 'url', 'body'];

		// Scrub emails from all fields
		fieldsToScrub.forEach(field => {
			if (request[field]) {
				request[field] = scrubEmails(request[field]);
			}
		});

		// Filter sensitive route data
		if (request.url && sensitiveRoutes.some(route => request.url.includes(route))) {
			request.data = '[Filtered]';
		}

		return event;
	},

	beforeBreadcrumb(breadcrumb, hint) {
		if (!breadcrumb) return null;
		// Filter out XHR breadcrumbs for specific domains
		if (breadcrumb.category === 'xhr') {
			const url = hint?.xhr?.url || breadcrumb?.data?.url;
			if (!url) return breadcrumb;

			if (ignoredDomainsAndPaths.some(domainOrPath => url.includes(domainOrPath))) {
				return null;
			}
		}
		return breadcrumb;
	},

	// Tracing
	tracesSampleRate: 0.1,

	// Session Replay
	replaysSessionSampleRate: 0.1,
	replaysOnErrorSampleRate: 1.0,
};

/** Determines if Sentry initialization is allowed. */
const initAllowed = () => {
	const deny = msg => !!console.warn(msg);

	if (!SENTRY_DSN) return deny('Sentry DSN not found in environment variables.');

	if (!ENVIRONMENT) return deny('Environment variable NODE_ENV is not set.');

	if (Sentry.isInitialized()) return deny('Sentry is already initialized.');

	return true;
};

/**
 * Initializes Sentry with given enabled state.
 *
 * ___
 *
 * - Attempts to initialize Sentry with the provided configuration.
 * - If initialization fails, logs the error to the console.
 *
 * ___
 *
 * @param {boolean} enabled - Determines whether Sentry is allowed to send events to sentry dashboard.
 * @returns {void}
 * @throws {void} - Catches and logs any errors internally instead of throwing
 */
const initializeSentry = enabled => {
	if (!initAllowed()) return;

	try {
		Sentry.init({ ...config, enabled });
	} catch (error) {
		console.group('Error initializing Sentry');
		console.warn(error);
		console.groupEnd();
	}
};

/** Returns Sentry enabled state */
const sentryEnabled = () => Sentry?.getClient()?.getOptions()?.enabled ?? false;

/** Called on Cookiebot consent form submit */
const onConsentChange = () => {
	const { statistics } = getCookieConsent();
	const enabled = sentryEnabled();

	// If sentry is in correct state, do nothing.
	if (statistics == enabled) return;

	// Sentry does not support dynamic enabling/disabling, hence the reload
	if (enabled)
		Sentry.close(); // Stops events from being sent to Sentry
	else if (statistics) location.reload();
};

// Listener for change in Cookie consent.
// Documentation -> https://www.cookiebot.com/en/developer/#h-event-handling
window.addEventListener('CookiebotOnConsentReady', onConsentChange);

// Init sentry according to the user's initial consent.
initializeSentry(getCookieConsent()?.statistics);
